import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DocumentExtractionResponse, DocumentNormalizationSummary, SubmissionDocument } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

function confidenceLabel(confidence: number | null): string {
  if (confidence === null) return '—';
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Draft interpretation only — never an academic approval view. No
 * approve/reject controls; that remains a later, separate faculty workflow.
 */
function DraftExtractionPanel({ extraction }: { extraction: DocumentExtractionResponse | undefined }) {
  if (!extraction?.run) {
    return <p className="text-muted text-small">No draft extraction yet.</p>;
  }

  return (
    <div className="draft-extraction-panel">
      <p className="text-small">
        <StatusBadge status={extraction.run.status} /> · {extraction.run.provider} ({extraction.run.providerModel}) ·{' '}
        {extraction.rows.length} rows · {extraction.fields.length} fields
        {extraction.run.needsReviewCount > 0 ? ` · ${extraction.run.needsReviewCount} need confirmation` : ''}
      </p>
      {extraction.run.safeFailureMessage && <p className="text-muted text-small">{extraction.run.safeFailureMessage}</p>}
      {extraction.fields.length > 0 && (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Field</th>
                <th>Raw text</th>
                <th>Confidence</th>
                <th>Review status</th>
              </tr>
            </thead>
            <tbody>
              {extraction.fields.map((f) => (
                <tr key={f.id}>
                  <td>{f.rowIndex ?? '—'}</td>
                  <td>{f.fieldType.replace(/_/g, ' ')}</td>
                  <td>{f.rawText ?? f.normalizedValue ?? '—'}</td>
                  <td>{confidenceLabel(f.applicationConfidence)}</td>
                  <td>
                    <StatusBadge status={f.reviewStatus} variant={f.reviewStatus === 'UNREVIEWED' ? 'muted' : 'warning'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface StudentDocumentsPageProps {
  backPath: string;
  backLabel: string;
}

export function StudentDocumentsPage({ backPath, backLabel }: StudentDocumentsPageProps) {
  const { studentId } = useParams<{ studentId: string }>();
  const [documents, setDocuments] = useState<
    Array<SubmissionDocument & { normalization?: DocumentNormalizationSummary }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [extractionByDocumentId, setExtractionByDocumentId] = useState<Record<string, DocumentExtractionResponse>>({});

  const load = () => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    api
      .listStudentDocuments(studentId)
      .then((res) => setDocuments(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load documents'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const toggleDraftExtraction = async (documentId: string) => {
    if (expandedDocId === documentId) {
      setExpandedDocId(null);
      return;
    }
    setExpandedDocId(documentId);
    if (!extractionByDocumentId[documentId]) {
      try {
        const result = await api.getDocumentExtraction(documentId);
        setExtractionByDocumentId((prev) => ({ ...prev, [documentId]: result }));
      } catch {
        // Authorized viewer, transient failure — panel shows "no draft yet" state.
      }
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Clinical Documents"
        subtitle="Authorized student document access"
        actions={
          <Link to={backPath} className="btn btn-secondary">
            ← {backLabel}
          </Link>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading documents…" />
      ) : documents.length === 0 ? (
        <div className="card">
          <p className="text-muted">No clinical documents uploaded for this student.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Uploaded</th>
                  <th>Filename</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Quality / Form</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <Fragment key={doc.id}>
                    <tr>
                      <td>{new Date(doc.createdAt).toLocaleString()}</td>
                      <td>{doc.originalFilename ?? 'Clinical sheet'}</td>
                      <td>{doc.documentType.replace(/_/g, ' ')}</td>
                      <td>
                        <StatusBadge status={doc.status} />
                      </td>
                      <td className="text-small">
                        {doc.normalization?.quality && (
                          <p>{doc.normalization.quality.replace(/_/g, ' ')}</p>
                        )}
                        {doc.normalization?.template && <p>{doc.normalization.template}</p>}
                        {doc.normalization?.warningMessages?.slice(0, 2).map((w) => (
                          <p key={w} className="text-muted">
                            {w}
                          </p>
                        ))}
                      </td>
                      <td>
                        {doc.status === 'READY' && (
                          <>
                            <a
                              href={api.documentContentUrl(doc.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary btn-sm"
                            >
                              View Original
                            </a>
                            {doc.normalization?.preparedPageAvailable && (
                              <a
                                href={api.documentPreparedPageUrl(doc.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary btn-sm"
                              >
                                Prepared Page
                              </a>
                            )}
                            {doc.normalization?.extractionReadiness === 'READY_FOR_EXTRACTION' && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => void toggleDraftExtraction(doc.id)}
                              >
                                {expandedDocId === doc.id ? 'Hide Draft Extraction' : 'View Draft Extraction'}
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                    {expandedDocId === doc.id && (
                      <tr>
                        <td colSpan={6}>
                          <DraftExtractionPanel extraction={extractionByDocumentId[doc.id]} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
